import { useCallback, useEffect, useRef } from 'react';

/**
 * Returns a stable callback to advance the LP wizard phase: immediately increments
 * phase, or after `delay` ms if `delay > 0`. A new delayed call replaces any
 * previous pending timeout. Delayed updates run only while the provider is still
 * mounted; pending timeouts are cleared on unmount.
 *
 * @param {React.Dispatch<React.SetStateAction<number>>} setPhase - Phase setter from `useState`.
 * @returns {(options?: { delay?: number, callBack?: (() => void)|null }) => void} Callback passed to the app context as `nextPhase`.
 */
export function useGamblingNextPhase(setPhase) {
    const isMountedRef = useRef(true);
    const phaseAdvanceTimeoutRef = useRef(null);

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;

            if (phaseAdvanceTimeoutRef.current !== null) {
                clearTimeout(phaseAdvanceTimeoutRef.current);
                phaseAdvanceTimeoutRef.current = null;
            }
        };
    }, []);

    return useCallback(
        ({ delay = 0, callBack = null } = {}) => {
            if (typeof callBack === 'function') {
                callBack();
            }

            if (delay === 0) {
                setPhase((prev) => prev + 1);
            } else {
                if (phaseAdvanceTimeoutRef.current !== null) {
                    clearTimeout(phaseAdvanceTimeoutRef.current);
                }

                phaseAdvanceTimeoutRef.current = setTimeout(() => {
                    phaseAdvanceTimeoutRef.current = null;

                    if (isMountedRef.current) {
                        setPhase((prev) => prev + 1);
                    }
                }, delay);
            }
        },
        [setPhase]
    );
}
